import React from "react";
import type { ColorCodingTemplate } from "@models/ColorCoding";

interface ColorCodingLegendProps {
  // Already-resolved active template (null = no color coding, renders nothing).
  template: ColorCodingTemplate | null;
}

/**
 * Legend for an active color coding template. Lists the buckets in order with a
 * color swatch, range label, and boundary text. Rendered below all other
 * footer content in both the preview and the dashboard widget.
 *
 * Styling matches the app's other chart legends: a circular color swatch
 * (like the grouped bar chart) with a `text-[13px] leading-[18px]
 * text-[#374151]` label, so legends stay visually consistent.
 */
const ColorCodingLegend: React.FC<ColorCodingLegendProps> = ({ template }) => {
  if (!template) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 px-2 py-2">
      {template.ranges.map((range) => (
        <div key={range.label} className="flex items-center gap-2.5">
          <div
            className="w-3 h-3 rounded-full flex-shrink-0 box-border"
            style={{ backgroundColor: range.bgColor }}
          />
          <span className="text-[13px] leading-[18px] text-[#374151] whitespace-nowrap">
            <span className="font-semibold">{range.label}</span>{" "}
            {range.boundaryText}
          </span>
        </div>
      ))}
    </div>
  );
};

export default ColorCodingLegend;
